import "./index.less";
import React, {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import classNames from "classnames";
import useScreenDesign, {
  screenType,
} from "@/Core/utils/hooks/useScreenDesign";
import { CloseIcon } from "../Icon";

type InputUISize = "mini" | "small" | "default" | "large";
type InputUIStatus = "error" | "warning" | null;

export interface InputUIRef {
  blur: () => void;
  focus: () => void;
  triggerFocus: () => void;
  triggreBlur: () => void;
}

export interface InputPlusProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "onBlur" | "onChange" | "onFocus" | "prefix" | "size"
  > {
  addAfter?: ReactNode;
  addBefore?: ReactNode;
  allowClear?: boolean;
  dataList?: string[] | false | null;
  error?: boolean;
  initFocus?: boolean;
  onBlur?: (event?: FocusEvent<HTMLInputElement>) => void;
  onChange?: (value: string, event: ChangeEvent<HTMLInputElement>) => void;
  onClear?: (event?: MouseEvent<HTMLButtonElement>) => void;
  onClickDropData?: ((value: string) => void) | false | null;
  onFocus?: (event?: FocusEvent<HTMLInputElement>) => void;
  onPressEnter?: (event: KeyboardEvent<HTMLInputElement>) => void;
  prefix?: ReactNode;
  size?: InputUISize;
  status?: InputUIStatus;
  suffix?: ReactNode;
  triggerDisabled?: boolean;
}

const getInitialValue = (
  value: InputPlusProps["value"],
  defaultValue: InputPlusProps["defaultValue"]
) => {
  if (value !== undefined && value !== null) {
    return String(value);
  }

  if (defaultValue !== undefined && defaultValue !== null) {
    return String(defaultValue);
  }

  return "";
};

const InputUI = forwardRef<InputUIRef, InputPlusProps>((props, ref) => {
  const mobile = useScreenDesign(screenType.mobile);
  const {
    addAfter,
    addBefore,
    allowClear,
    className,
    dataList = [],
    disabled,
    error,
    initFocus,
    onBlur,
    onChange,
    onClear,
    onClickDropData,
    onFocus,
    onKeyDown,
    onPressEnter,
    prefix,
    readOnly,
    size,
    status,
    style,
    suffix,
    triggerDisabled,
    value,
    defaultValue,
    ...inputProps
  } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const isControlled = value !== undefined;
  const [innerValue, setInnerValue] = useState(
    getInitialValue(value, defaultValue)
  );
  const [focused, setFocused] = useState(false);
  const inputSize = size ?? (mobile ? "small" : "default");
  const inputValue = isControlled ? value : innerValue;
  const currentValue = inputValue === undefined || inputValue === null
    ? ""
    : String(inputValue);
  const hasError = error || status === "error";
  const safeDataList = Array.isArray(dataList) ? dataList : [];
  const showDataList = !triggerDisabled && focused && safeDataList.length > 0;
  const showClear = Boolean(
    allowClear && currentValue && !disabled && !readOnly
  );

  useEffect(() => {
    if (initFocus) {
      inputRef.current?.focus();
    }
  }, [initFocus]);

  const triggerFocus = () => {
    inputRef.current?.focus();
  };

  const triggreBlur = () => {
    inputRef.current?.blur();
  };

  useImperativeHandle(ref, () => ({
    blur: triggreBlur,
    focus: triggerFocus,
    triggerFocus,
    triggreBlur,
  }));

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.currentTarget.value;

    if (!isControlled) {
      setInnerValue(nextValue);
    }

    onChange?.(nextValue, event);
  };

  const handleClear = (event: MouseEvent<HTMLButtonElement>) => {
    if (disabled || readOnly) {
      return;
    }

    if (!isControlled) {
      setInnerValue("");
    }

    if (inputRef.current) {
      inputRef.current.value = "";
    }

    onChange?.(
      "",
      {
        ...event,
        currentTarget: inputRef.current,
        target: inputRef.current,
      } as unknown as ChangeEvent<HTMLInputElement>
    );
    onClear?.(event);
    inputRef.current?.focus();
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    setFocused(true);
    onFocus?.(event);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    setFocused(false);
    onBlur?.(event);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      onPressEnter?.(event);
    }

    onKeyDown?.(event);
  };

  return (
    <span
      className={classNames(
        "input-ui",
        `input-ui-size-${inputSize}`,
        {
          "input-ui-disabled": disabled,
          "input-ui-error": hasError,
          "input-ui-focused": focused,
          "input-ui-readonly": readOnly,
          "input-ui-warning": status === "warning",
        },
        className
      )}
      data-status={status || undefined}
      style={style}
    >
      {addBefore ? (
        <span className="input-ui-add-before">{addBefore}</span>
      ) : null}
      {prefix ? <span className="input-ui-prefix">{prefix}</span> : null}
      <input
        {...inputProps}
        ref={inputRef}
        aria-invalid={hasError || undefined}
        className="input-ui-native"
        disabled={disabled}
        onBlur={handleBlur}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        readOnly={readOnly}
        value={currentValue}
      />
      {showClear ? (
        <button
          aria-label="Clear"
          className="input-ui-clear"
          type="button"
          onClick={handleClear}
        >
          <CloseIcon />
        </button>
      ) : null}
      {suffix ? <span className="input-ui-suffix">{suffix}</span> : null}
      {addAfter ? (
        <span className="input-ui-add-after">{addAfter}</span>
      ) : null}
      {showDataList ? (
        <div className="input-ui-data-list">
          {safeDataList.map((item, index) => (
            <button
              className="input-ui-data-item"
              key={`${item}-${index}`}
              type="button"
              onClick={() => {
                if (typeof onClickDropData === "function") {
                  onClickDropData(item);
                }
              }}
              onMouseDown={(event) => {
                event.preventDefault();
              }}
            >
              {item?.length <= 47 ? (
                item
              ) : (
                <>
                  <span className="r">{item.slice(0, 40)}</span>
                  <span className="l">{item.slice(40)}</span>
                </>
              )}
            </button>
          ))}
        </div>
      ) : null}
    </span>
  );
});

InputUI.displayName = "InputUI";

export default InputUI;
